import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { Eye, EyeOff, Mail, Lock, Gamepad2, Crown, ArrowRight, Sparkles, Gift, Zap } from 'lucide-react';
import api from '../../services/api';
import '../../styles/casino-redesign.css';
import '../../styles/pirate-game-lobby-v3.css';
import '../../styles/pirate-live-fixes.css';

export default function AuthPage() {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAgentPortal = window.location.hostname === 'agent.vegaslobby.vip';
  
  const [mode, setMode] = useState('login');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [referralCode, setReferralCode] = useState(searchParams.get('ref') || '');
  // Track the referral channel (?src=qr|share|copy|...) so admin analytics
  // can break down which invite method drives the most signups.
  const [referralSource] = useState(searchParams.get('src') || '');
  const [emailError, setEmailError] = useState('');

  // Iter 142.8: surface tenant slug at component scope so the Google
  // OAuth start handler can pass it through to the backend.
  const tenantSlug = (() => {
    const slug = window.location.pathname.startsWith('/') ? window.location.pathname.split('/').filter(Boolean)[0] : '';
    const reserved = new Set(['auth', 'admin', 'app', 'referral-staff', 'staff-portal', 'finance', 'agent']);
    return slug && !reserved.has(slug) ? slug : '';
  })();
  useEffect(() => {
    if (user) {
      // Honour ?next=<path> when the player landed here from a payment
      // return (DepositReturnPage) — send them straight back to their
      // wallet so the deposit flow completes seamlessly.
      const next = searchParams.get('next');
      if (next && next.startsWith('/') && !next.startsWith('//')) {
        navigate(next, { replace: true });
        return;
      }
      if (['admin', 'moderator', 'support'].includes(user.role)) {
        navigate('/admin');
      } else if (user.role === 'referral_agent') {
        navigate('/referral-staff');
      } else if (user.role === 'staff') {
        navigate('/staff-portal');
      } else {
        navigate('/app');
      }
    }
  }, [user, navigate, searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setEmailError('');
    const identifier = email.trim();
    if (mode === 'forgot') {
      if (!identifier) {
        toast.error('Please enter your account email');
        return;
      }

      if (resetSent && (!resetCode.trim() || !resetPassword)) {
        toast.error('Please enter the reset code and your new password');
        return;
      }

      setLoading(true);
      try {
        if (!resetSent) {
          const res = await api.post('/api/auth/forgot-password/start', { identifier });
          setResetSent(true);
          toast.success(res.data?.message || 'If that account exists, a reset code has been sent.');
        } else {
          const res = await api.post('/api/auth/forgot-password/verify', {
            identifier,
            code: resetCode.trim(),
            new_password: resetPassword,
          });
          toast.success(res.data?.message || 'Password updated. Please sign in.');
          setMode('login');
          setResetSent(false);
          setResetCode('');
          setResetPassword('');
          setPassword('');
        }
      } catch (err) {
        const detail = err.response?.data?.detail;
        const backendMessage = err.response?.data?.message;
        const message = typeof detail === 'string' ? detail :
                        typeof backendMessage === 'string' ? backendMessage :
                        Array.isArray(detail) ? detail.map(item => item.msg).join(', ') :
                        'Password reset failed';
        toast.error(message);
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!email || !password) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    try {
      if (mode === 'login') {
        await login(email, password);
        toast.success('Welcome back!');
      } else {
        await register(email, password, null, null, referralCode, null, referralSource);
        toast.success('Account created! Welcome to Vegas Lobby!');
      }
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      // Backend returns 'message' field in contract-compliant error envelope
      const backendMessage = err.response?.data?.message;
      const message = typeof detail === 'string' ? detail :
                      typeof backendMessage === 'string' ? backendMessage :
                      Array.isArray(detail) ? detail.map(e => e.msg).join(', ') :
                      'Authentication failed';
      // 409 Conflict on signup → inline error under the email field
      if (mode === 'register' && status === 409 && /email/i.test(message)) {
        setEmailError(message);
        toast.error(message);
      } else {
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Iter 142.8: direct Google OAuth flow.
    // The redirect_uri is computed from window.location.origin so the
    // SAME code works on preview + prod + localhost.
    const redirectUri = window.location.origin + '/auth/google';
    try {
      const res = await api.post('/api/auth/google/start', {
        redirect_uri: redirectUri,
        referral_code: referralCode || undefined,
        tenant_slug: tenantSlug || undefined,
      });
      const url = res.data?.auth_url;
      if (!url) throw new Error('OAuth not configured');
      window.location.href = url;
    } catch (err) {
      const data = err.response?.data || {};
      toast.error(data.message || data.detail || err.message || 'Google sign-in is not configured');
    }
  };

  return (
    <div className="vl-auth-shell vl-pirate-auth vl-auth-cinematic min-h-screen text-white lg:grid lg:grid-cols-[minmax(0,1.15fr)_minmax(420px,.85fr)]" data-casino-theme="pirate" data-auth-mode={mode} data-testid="auth-page">
      {/* Left Panel - Hero */}
      <div className="vl-auth-hero hidden lg:flex relative overflow-hidden">
        <div className="vl-auth-hero__keyart absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('/vegas-pirate-lobby-bg.webp')" }} />
        <div className="vl-auth-reelstorm" aria-hidden="true">
          <i>7</i><i>♠</i><i>★</i><i>💰</i>
        </div>
        <div className="vl-auth-coin-rain" aria-hidden="true">
          {Array.from({ length: 14 }).map((_, idx) => <span key={idx} style={{ '--i': idx }} />)}
        </div>
        <div className="vl-auth-wave-glow" aria-hidden="true" />
        <div className="vl-auth-live-character" aria-hidden="true">
          <span />
          <img src="/vegas-pirate-captain-live.webp" alt="" draggable="false" />
        </div>
        <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, rgba(3,10,7,.97), rgba(3,10,7,.76) 48%, rgba(3,10,7,.2)), linear-gradient(0deg, rgba(2,7,5,.9), transparent 55%)' }} />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(212,175,55,0.06)_1px,transparent_1px),linear-gradient(90deg,rgba(212,175,55,0.05)_1px,transparent_1px)] bg-[size:42px_42px] opacity-20" />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(circle at 25% 30%, rgba(212,175,55,0.18), transparent 40%), radial-gradient(circle at 70% 70%, rgba(108,92,231,0.12), transparent 45%)',
          }}
        />
        <div className="vl-auth-hero__copy relative z-10 flex flex-col justify-center p-12 max-w-lg">
          <div className="flex items-center gap-3 mb-8">
            <img
              src="/vegas-lobby-logo.png"
              alt="Vegas Lobby"
              className="h-14 w-auto"
              data-testid="auth-brand-logo"
              style={{ filter: 'drop-shadow(0 4px 12px rgba(212,175,55,0.35))' }}
            />
          </div>

          {/* Attention-grabbing bonus chip — anchors the hero with the
              specific value-prop the user wants front and centre. */}
          <div
            className="vl-auth-gold-chip inline-flex items-center gap-2 self-start px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.16em] mb-4"
            style={{
              background: 'rgba(212,175,55,0.16)',
              border: '1px solid rgba(212,175,55,0.5)',
              color: '#F5D87A',
              boxShadow: '0 6px 20px rgba(212,175,55,0.18)',
            }}
            data-testid="hero-bonus-chip"
          >
            <Sparkles className="w-3.5 h-3.5" />
            Live pirate casino entry
          </div>

          <h1 className="vl-auth-headline text-5xl font-bold mb-4 leading-tight">
            The treasure table is live.<br />
            <span className="gold-text">Come aboard.</span>
          </h1>

          {/* Legit-player hero card — no signup FP bonus copy. */}
          <div
            className="vl-auth-proof-card rounded-2xl p-5 mb-6 relative overflow-hidden"
            style={{
              background:
                'linear-gradient(135deg, rgba(212,175,55,0.18) 0%, rgba(245,216,122,0.10) 50%, rgba(108,92,231,0.10) 100%)',
              border: '1px solid rgba(212,175,55,0.4)',
              boxShadow: '0 14px 40px rgba(212,175,55,0.18)',
            }}
            data-testid="hero-bonus-card"
          >
            <div
              className="absolute -top-4 -right-4 w-24 h-24 rounded-full opacity-30 blur-2xl"
              style={{ background: 'radial-gradient(circle, #F5D87A, transparent)' }}
            />
            <div className="flex items-start gap-3 relative">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                style={{
                  background: 'linear-gradient(135deg, #D4AF37, #F5D87A)',
                  boxShadow: '0 8px 24px rgba(212,175,55,0.35)',
                }}
              >
                <Gift className="w-6 h-6 text-black" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#F5D87A]/80 font-semibold">
                  Verified Player Lounge
                </p>
                <p className="text-2xl font-black text-white leading-tight mt-0.5">
                  Real players only
                </p>
                <p className="text-[12.5px] text-gray-300 mt-1.5 leading-relaxed">
                  Legit casino action for genuine players. Scammers stay away — every signup enters a fair-play lobby.
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 mt-1">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <Gamepad2 className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-xs text-gray-200 font-semibold">50+ Games</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <Crown className="w-4 h-4 text-[#10B981]" />
              <span className="text-xs text-gray-200 font-semibold">VIP Rewards</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <Zap className="w-4 h-4 text-[#F5D87A]" />
              <span className="text-xs text-gray-200 font-semibold">Instant Cashout</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Auth Form */}
      <div className="vl-auth-form w-full min-h-screen flex items-center justify-center px-5 py-7 lg:px-10" style={{ background: 'radial-gradient(circle at 50% 0%, rgba(22,199,132,0.10), transparent 32%), radial-gradient(circle at 100% 100%, rgba(212,175,55,0.10), transparent 38%), #050b09' }}>
        <div className="vl-auth-mobile-cinematic lg:hidden" aria-hidden="true">
          <span className="vl-auth-mobile-cinematic__moon" />
          <span className="vl-auth-mobile-cinematic__mist" />
          <img src="/vegas-pirate-captain-live.webp" alt="" draggable="false" />
        </div>
        <div className="vl-auth-form__content w-full max-w-sm mx-auto">
          {/* Mobile Logo */}
          <div className="flex items-center justify-center mb-6">
            <img
              src="/vegas-lobby-logo.png"
              alt="Vegas Lobby"
              className="h-24 w-auto object-contain"
              data-testid="auth-brand-logo-mobile"
            />
          </div>

          <div className="vl-auth-treasure-card vl-auth-form-card rounded-[28px] p-5 sm:p-7 border border-white/10 shadow-[0_24px_70px_rgba(0,0,0,0.42)]" style={{ background: 'rgba(19,24,34,0.88)' }}>
            <div className="vl-auth-card-crest" aria-hidden="true"><span>☠</span></div>
            <div className="vl-auth-card-cinematic" aria-hidden="true">
              <div className="vl-auth-card-cinematic__logo">
                <img src="/vegas-lobby-logo.png" alt="" draggable="false" />
                <span>{mode === 'register' ? 'Join the crew' : 'Welcome aboard'}</span>
              </div>
              <div className="vl-auth-card-cinematic__slots">
                {['777', 'BAR', '💰'].map((finalSymbol, index) => (
                  <i key={`auth-card-${finalSymbol}-${index}`} style={{ '--reel': index }}>
                    <em>
                      <span>♠</span>
                      <span>7</span>
                      <span>♦</span>
                      <span>BAR</span>
                      <span>💰</span>
                      <span>{finalSymbol}</span>
                    </em>
                  </i>
                ))}
              </div>
              <div className="vl-auth-card-cinematic__lady" />
              <div className="vl-auth-card-cinematic__cards">
                {['A♠', 'K♦', 'Q♥'].map((card, index) => (
                  <b key={`auth-card-mini-${card}`} style={{ '--card': index }}>{card}</b>
                ))}
              </div>
              <div className="vl-auth-card-cinematic__coins">
                <span /><span /><span /><span />
              </div>
            </div>
            <p className="vl-auth-card-kicker">Vegas Lobby · Captain's Deck</p>
            <h2 className="vl-auth-card-title text-3xl font-black text-center mb-2 tracking-tight">
              {mode === 'forgot' ? 'Reset Password' : mode === 'login' ? (isAgentPortal ? 'Referral Agent Portal' : 'Welcome Back') : 'Create Account'}
            </h2>
            <p className="vl-auth-card-subtitle text-gray-400 text-center mb-5 text-sm">
              {mode === 'forgot'
                ? resetSent
                  ? 'Enter the code from your email and choose a new password'
                  : 'We will email a reset code if the account exists'
                : mode === 'login'
                  ? (isAgentPortal ? 'Sign in to manage referrals, players, and earnings.' : 'Sign in to continue playing')
                  : 'Join thousands of players'}
            </p>

            {/* Iter 142.14: high-energy "$5 Free Play" promo strip — visible in
                BOTH login and register modes so existing users feel the
                ongoing campaign and new ones immediately see the value of
                signing up. Animated gold glow + sparkle + clear CTA copy. */}
            {!isAgentPortal && mode === 'login' && (
              <button
                type="button"
                onClick={() => setMode('register')}
                className="w-full mb-6 rounded-2xl px-4 py-3.5 flex items-center gap-3 transition hover:scale-[1.015] active:scale-[0.99] text-left relative overflow-hidden group"
                style={{
                  background:
                    'linear-gradient(135deg, rgba(212,175,55,0.22) 0%, rgba(245,216,122,0.12) 50%, rgba(108,92,231,0.12) 100%)',
                  border: '1px solid rgba(212,175,55,0.5)',
                  boxShadow: '0 10px 28px rgba(212,175,55,0.18)',
                }}
                data-testid="auth-bonus-promo"
              >
                <span
                  className="absolute -inset-1 opacity-25 blur-2xl group-hover:opacity-40 transition"
                  style={{
                    background:
                      'radial-gradient(circle at 18% 50%, #F5D87A, transparent 55%)',
                    pointerEvents: 'none',
                  }}
                />
                <span
                  className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 relative"
                  style={{
                    background: 'linear-gradient(135deg, #D4AF37, #F5D87A)',
                    boxShadow: '0 6px 18px rgba(212,175,55,0.4)',
                  }}
                >
                  <Gift className="w-5 h-5 text-black" />
                </span>
                <span className="flex-1 min-w-0 relative">
                  <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] font-bold" style={{ color: '#F5D87A' }}>
                    <Sparkles className="w-3 h-3" />
                    {mode === 'login' ? 'New here?' : 'Fair-play crew'}
                  </span>
                  <span className="block text-[15px] font-black text-white leading-tight mt-0.5">
                    Join the verified Vegas Lobby crew
                  </span>
                  <span className="block text-[11.5px] text-gray-300 mt-0.5">
                    Fast access · Fair play · Scammers stay away
                  </span>
                </span>
                <ArrowRight className="w-4 h-4 text-[#F5D87A] shrink-0 relative" />
              </button>
            )}

            {/* Mode Toggle */}
            {mode === 'forgot' ? (
              <button
                type="button"
                onClick={() => {
                  setMode('login');
                  setResetSent(false);
                  setResetCode('');
                  setResetPassword('');
                  setEmailError('');
                }}
                className="vl-auth-board-btn w-full mb-6 py-2 rounded-lg bg-[#0B0E14] text-sm font-medium text-[#D4AF37] hover:bg-white/5 transition"
                data-testid="back-to-login"
              >
                Back to Sign In
              </button>
            ) : isAgentPortal ? null : (
              <div className="vl-auth-board-tabs flex rounded-lg bg-[#0B0E14] p-1 mb-6">
                <button
                  onClick={() => setMode('login')}
                  className={`vl-auth-board-tab flex-1 py-2 rounded-md text-sm font-medium transition-all ${
                    mode === 'login' ? 'bg-[#D4AF37] text-black' : 'text-gray-400 hover:text-white'
                  }`}
                  data-active={mode === 'login' ? 'true' : 'false'}
                  data-testid="login-tab"
                >
                  Sign In
                </button>
                <button
                  onClick={() => setMode('register')}
                  className={`vl-auth-board-tab flex-1 py-2 rounded-md text-sm font-medium transition-all ${
                    mode === 'register' ? 'bg-[#D4AF37] text-black' : 'text-gray-400 hover:text-white'
                  }`}
                  data-active={mode === 'register' ? 'true' : 'false'}
                  data-testid="register-tab"
                >
                  Sign Up
                </button>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">{mode === 'forgot' ? 'Account Email' : 'Email'}</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    data-testid="email-input"
                    type={mode === 'forgot' ? 'text' : 'email'}
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); if (emailError) setEmailError(''); }}
                    placeholder={mode === 'forgot' ? 'Enter your account email' : 'Enter your email'}
                    className={`pl-10 bg-[#0B0E14] text-white h-11 ${emailError ? 'border-red-500 focus-visible:ring-red-500' : 'border-white/10'}`}
                    aria-invalid={!!emailError}
                    aria-describedby={emailError ? 'email-error' : undefined}
                  />
                </div>
                {emailError && (
                  <p
                    id="email-error"
                    className="mt-1.5 text-xs text-red-400 flex items-center gap-1"
                    data-testid="email-error"
                  >
                    {emailError}
                    {mode === 'register' && /in use|registered/i.test(emailError) && (
                      <button
                        type="button"
                        onClick={() => { setMode('login'); setEmailError(''); }}
                        className="underline text-red-300 hover:text-red-200 ml-1"
                        data-testid="switch-to-login-link"
                      >
                        Sign in instead →
                      </button>
                    )}
                  </p>
                )}
              </div>

              {mode !== 'forgot' && (
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    data-testid="password-input"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="pl-10 pr-10 bg-[#0B0E14] border-white/10 text-white h-11"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              )}

              {mode === 'forgot' && resetSent && (
                <>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1.5">Reset Code</label>
                    <Input
                      data-testid="reset-code-input"
                      type="text"
                      inputMode="numeric"
                      value={resetCode}
                      onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="6-digit code"
                      className="bg-[#0B0E14] border-white/10 text-white h-11"
                      maxLength={6}
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1.5">New Password</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                      <Input
                        data-testid="reset-password-input"
                        type={showResetPassword ? 'text' : 'password'}
                        value={resetPassword}
                        onChange={(e) => setResetPassword(e.target.value)}
                        placeholder="Create a new password"
                        className="pl-10 pr-10 bg-[#0B0E14] border-white/10 text-white h-11"
                      />
                      <button
                        type="button"
                        onClick={() => setShowResetPassword(!showResetPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                      >
                        {showResetPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <p className="mt-1.5 text-[11px] text-gray-500">
                      Use at least 8 characters with uppercase, lowercase, and a number.
                    </p>
                  </div>
                </>
              )}

              {mode === 'register' && (
                <div>
                  <label className="block text-sm text-gray-400 mb-1.5">Referral Code (Optional)</label>
                  <Input
                    data-testid="referral-input"
                    type="text"
                    value={referralCode}
                    onChange={(e) => setReferralCode(e.target.value)}
                    placeholder="Enter referral code"
                    className="bg-[#0B0E14] border-white/10 text-white h-11"
                  />
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="vl-auth-board-btn w-full h-11 rounded-full bg-[#D4AF37] text-black font-semibold hover:bg-[#E5C158] transition-colors"
                data-testid="submit-btn"
              >
                {loading ? (
                  <div className="spinner w-5 h-5 border-2 border-black/30 border-t-black" />
                ) : (
                  <>
                    {mode === 'forgot'
                      ? resetSent ? 'Reset Password' : 'Send Reset Code'
                      : mode === 'login' ? 'Sign In' : 'Create Account'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </form>

            {mode === 'login' && (
              <p className="text-center text-sm text-gray-500 mt-4">
                <button
                  type="button"
                  onClick={() => {
                    setMode('forgot');
                    setResetSent(false);
                    setResetCode('');
                    setResetPassword('');
                    setPassword('');
                    setEmailError('');
                  }}
                  className="text-[#D4AF37] hover:underline"
                  data-testid="forgot-password-btn"
                >
                  Forgot password?
                </button>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
